import {
  LoadStopsItem,
  LoadStopsItemTypes,
  NormalizedDropoff,
  NormalizedLoad,
  NormalizedPickup,
  createInitLoad,
  normalizeLoad,
} from 'types/Load';
import { Maybe, MaybeUndef } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';

import {
  parseFormDate,
  validateDeliveryOnOrAfterPickupDate,
} from '../utils/stopValidation';

function toDateString(value: unknown): string {
  if (!value) {
    return '';
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  return String(value);
}

function toMaybeDate(value: unknown): Maybe<Date> {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  const parsed = new Date(String(value));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function defaultMcleodPickupConsignee(): Pick<
  NormalizedLoad,
  'pickup' | 'consignee'
> {
  const base = normalizeLoad(TMS.McleodEnterprise, createInitLoad());

  return {
    pickup: base.pickup,
    consignee: base.consignee,
  };
}

export const MAX_MCLEOD_ROUTE_STOPS = 5;

export function buildMcleodStopsFromPickupAndConsignee(
  pickup: MaybeUndef<NormalizedPickup>,
  consignee: MaybeUndef<NormalizedDropoff>
): LoadStopsItem[] {
  const defaultStops = createInitLoad().stops!;

  return [
    buildMcleodRouteStopFromFlatLocation(
      0,
      pickup,
      LoadStopsItemTypes.Pickup,
      defaultStops[0]
    ),
    buildMcleodRouteStopFromFlatLocation(
      1,
      consignee,
      LoadStopsItemTypes.Dropoff,
      defaultStops[1]
    ),
  ];
}

function buildMcleodRouteStopFromFlatLocation(
  index: number,
  location: MaybeUndef<NormalizedPickup | NormalizedDropoff>,
  stopType: LoadStopsItemTypes,
  fallback: LoadStopsItem
): LoadStopsItem {
  if (!location) {
    return {
      ...fallback,
      order: index,
      stopNumber: index,
      stopType,
    };
  }

  return {
    ...fallback,
    order: index,
    stopNumber: index,
    stopType,
    externalTMSID: location.externalTMSID ?? '',
    contact: location.contact ?? '',
    phone: location.phone ?? '',
    email: location.email ?? '',
    refNumber: location.refNumber ?? '',
    apptRequired: location.apptRequired ?? false,
    apptStartTime: toDateString(location.apptStartTime),
    // McLeod maps SchedArriveLate to both apptEndTime and mustDeliver on dropoff;
    // prefer the schedule field, then fall back so edit hydrate does not drop it.
    apptEndTime:
      toDateString(location.apptEndTime) ||
      (stopType === LoadStopsItemTypes.Dropoff
        ? toDateString((location as NormalizedDropoff).mustDeliver)
        : ''),
    apptNote: location.apptNote ?? '',
    timezone: location.timezone ?? '',
    additionalReferences: location.additionalReferences ?? [],
    readyTime:
      stopType === LoadStopsItemTypes.Pickup
        ? toDateString((location as NormalizedPickup).readyTime)
        : toDateString(fallback.readyTime),
    mustDeliver:
      stopType === LoadStopsItemTypes.Dropoff
        ? toDateString((location as NormalizedDropoff).mustDeliver) ||
          toDateString(location.apptEndTime)
        : toDateString(fallback.mustDeliver),
    address: {
      name: location.name ?? '',
      addressLine1: location.addressLine1 ?? '',
      addressLine2: location.addressLine2 ?? '',
      city: location.city ?? '',
      state: location.state ?? '',
      zip: location.zipCode ?? '',
      country: location.country ?? '',
      timezone: location.timezone ?? '',
      isVerified: false,
    },
  };
}

export function createMcleodIntermediateStop(order: number): LoadStopsItem {
  const template = createInitLoad().stops![1];

  return {
    ...template,
    order,
    stopNumber: order,
    stopType: LoadStopsItemTypes.Stop,
    refNumber: '',
    additionalReferences: [],
  };
}

export function normalizeMcleodRouteStopTypes(
  stops: LoadStopsItem[]
): LoadStopsItem[] {
  if (stops.length === 0) {
    return stops;
  }

  return stops.map((stop, index) => {
    let stopType = LoadStopsItemTypes.Stop;
    if (index === 0) {
      stopType = LoadStopsItemTypes.Pickup;
    } else if (index === stops.length - 1) {
      stopType = LoadStopsItemTypes.Dropoff;
    }

    return {
      ...stop,
      order: index,
      stopNumber: index,
      stopType,
    };
  });
}

export function syncMcleodRouteStopsToPickupConsignee(
  stops: LoadStopsItem[]
): Pick<NormalizedLoad, 'pickup' | 'consignee'> {
  const defaults = defaultMcleodPickupConsignee();

  if (stops.length < 2) {
    return defaults;
  }

  const firstStop = stops[0];
  const lastStop = stops[stops.length - 1];

  return {
    pickup: flatPickupFromRouteStop(firstStop, defaults.pickup),
    consignee: flatDropoffFromRouteStop(lastStop, defaults.consignee),
  };
}

function flatPickupFromRouteStop(
  stop: LoadStopsItem,
  base: NormalizedPickup
): NormalizedPickup {
  return {
    ...base,
    externalTMSID: stop.externalTMSID ?? '',
    name: stop.address?.name ?? '',
    addressLine1: stop.address?.addressLine1 ?? '',
    addressLine2: stop.address?.addressLine2 ?? '',
    city: stop.address?.city ?? '',
    state: stop.address?.state ?? '',
    zipCode: stop.address?.zip ?? '',
    country: stop.address?.country ?? '',
    contact: stop.contact ?? '',
    phone: stop.phone ?? '',
    email: stop.email ?? '',
    refNumber: stop.refNumber ?? '',
    apptRequired: stop.apptRequired ?? false,
    apptStartTime: toMaybeDate(stop.apptStartTime),
    apptEndTime: toMaybeDate(stop.apptEndTime),
    apptNote: stop.apptNote ?? '',
    timezone: stop.timezone ?? stop.address?.timezone ?? '',
    additionalReferences: stop.additionalReferences ?? [],
    readyTime: toMaybeDate(stop.readyTime),
  };
}

function flatDropoffFromRouteStop(
  stop: LoadStopsItem,
  base: NormalizedDropoff
): NormalizedDropoff {
  return {
    ...base,
    externalTMSID: stop.externalTMSID ?? '',
    name: stop.address?.name ?? '',
    addressLine1: stop.address?.addressLine1 ?? '',
    addressLine2: stop.address?.addressLine2 ?? '',
    city: stop.address?.city ?? '',
    state: stop.address?.state ?? '',
    zipCode: stop.address?.zip ?? '',
    country: stop.address?.country ?? '',
    contact: stop.contact ?? '',
    phone: stop.phone ?? '',
    email: stop.email ?? '',
    refNumber: stop.refNumber ?? '',
    apptRequired: stop.apptRequired ?? false,
    apptStartTime: toMaybeDate(stop.apptStartTime),
    apptEndTime: toMaybeDate(stop.apptEndTime),
    apptNote: stop.apptNote ?? '',
    timezone: stop.timezone ?? stop.address?.timezone ?? '',
    additionalReferences: stop.additionalReferences ?? [],
    // Keep mustDeliver aligned with apptEndTime (same McLeod SchedArriveLate source).
    mustDeliver: toMaybeDate(stop.mustDeliver) ?? toMaybeDate(stop.apptEndTime),
  };
}

export function isMcleodMultiStopRoute(
  stops: MaybeUndef<LoadStopsItem[]>
): boolean {
  return (stops?.length ?? 0) > 2;
}

// Copy load-level PO onto stops missing a per-stop PO when expanding past 2 stops.
export function seedMcleodStopRefNumbersFromPoNums(
  stops: LoadStopsItem[],
  poNums: MaybeUndef<string>
): LoadStopsItem[] {
  const po = poNums?.trim();
  if (!po || stops.length <= 2) {
    return stops;
  }

  return stops.map((stop) => {
    if (stop.refNumber?.trim()) {
      return stop;
    }

    return {
      ...stop,
      refNumber: po,
    };
  });
}

// One-time migration when a route expands past two stops: seed empty stop PO fields,
// then clear load-level PO so hidden form state cannot re-seed on submit.
export function migrateMcleodLoadLevelPoNumsToRouteStops(
  stops: LoadStopsItem[],
  poNums: MaybeUndef<string>
): { stops: LoadStopsItem[]; poNums: string } {
  const seededStops = seedMcleodStopRefNumbersFromPoNums(stops, poNums);

  if (!isMcleodMultiStopRoute(seededStops) || !poNums?.trim()) {
    return { stops: seededStops, poNums: poNums ?? '' };
  }

  return { stops: seededStops, poNums: '' };
}

// Inverse of migrate when a route collapses back to two stops: if every stop PO
// matches, move it back to load-level poNums and clear duplicated stop fields.
export function restoreMcleodLoadLevelPoNumsFromRouteStops(
  stops: LoadStopsItem[],
  poNums: MaybeUndef<string>
): { stops: LoadStopsItem[]; poNums: string } {
  if (isMcleodMultiStopRoute(stops)) {
    return { stops, poNums: poNums ?? '' };
  }

  const existingPo = poNums?.trim();
  if (existingPo) {
    return { stops, poNums: existingPo };
  }

  const trimmedRefs = stops.map((stop) => stop.refNumber?.trim() ?? '');

  if (trimmedRefs.every((ref) => !ref)) {
    return { stops, poNums: '' };
  }

  if (trimmedRefs.some((ref) => !ref)) {
    return { stops, poNums: '' };
  }

  const uniqueRefs = new Set(trimmedRefs);
  if (uniqueRefs.size !== 1) {
    return { stops, poNums: '' };
  }

  const sharedPo = trimmedRefs[0];
  return {
    stops: stops.map((stop) => ({
      ...stop,
      refNumber:
        stop.refNumber?.trim() === sharedPo ? '' : (stop.refNumber ?? ''),
    })),
    poNums: sharedPo,
  };
}

export function mcleodStopHasRouteContent(
  stop: MaybeUndef<LoadStopsItem>
): boolean {
  if (!stop) {
    return false;
  }

  return Boolean(
    stop.externalTMSID?.trim() ||
    stop.refNumber?.trim() ||
    stop.address?.name?.trim() ||
    stop.address?.city?.trim() ||
    stop.address?.addressLine1?.trim()
  );
}

export function mcleodFlatLocationHasRouteContent(
  location: MaybeUndef<NormalizedPickup | NormalizedDropoff>
): boolean {
  if (!location) {
    return false;
  }

  return Boolean(
    location.externalTMSID?.trim() ||
    location.refNumber?.trim() ||
    location.name?.trim() ||
    location.city?.trim() ||
    location.addressLine1?.trim()
  );
}

export function mcleodRouteEndsHaveContent(stops: LoadStopsItem[]): boolean {
  if (stops.length < 2) {
    return false;
  }

  return (
    mcleodStopHasRouteContent(stops[0]) ||
    mcleodStopHasRouteContent(stops[stops.length - 1])
  );
}

/** Prefer route stops when populated; otherwise hydrate from legacy pickup/consignee. */
export function mcleodRouteStopLabel(
  stops: LoadStopsItem[],
  index: number
): string {
  if (index <= 0) {
    return 'Pickup';
  }

  if (index >= stops.length - 1) {
    return 'Consignee';
  }

  return `Stop ${index}`;
}

export function mcleodRouteStopCollapsedSummary(
  stop: MaybeUndef<LoadStopsItem>
): string {
  if (!stop) {
    return '';
  }

  const name = stop.address?.name?.trim() ?? '';
  const city = stop.address?.city?.trim() ?? '';
  const state = stop.address?.state?.trim() ?? '';
  const place = [city, state].filter(Boolean).join(', ');

  if (name && place) {
    return `${name} · ${place}`;
  }

  return name || place;
}

/** Expand the stop collapsible matching the first `stops.N` validation path. */
export function mcleodActiveStopTabFromErrors(
  errors: ReadonlyArray<{ path: string }>
): string | undefined {
  for (const { path } of errors) {
    const match = /^stops\.(\d+)/.exec(String(path));
    if (match) {
      return `stop-${match[1]}`;
    }
  }

  return undefined;
}

export function validateMcleodMonotonicRouteStopTimes(
  stops: LoadStopsItem[]
): { ok: true } | { ok: false; message: string; pathIndex: number } {
  for (let i = 0; i < stops.length - 1; i++) {
    const currentStop = stops[i];
    const nextStop = stops[i + 1];
    const currentEnd = parseFormDate(currentStop.apptEndTime);
    const currentTime = currentEnd
      ? currentStop.apptEndTime
      : currentStop.apptStartTime;
    const nextTime = nextStop.apptStartTime;

    const result = validateDeliveryOnOrAfterPickupDate(currentTime, nextTime, {
      compareTime: true,
    });

    if (!result.ok) {
      return {
        ok: false,
        message: `${mcleodRouteStopLabel(stops, i + 1)} must be scheduled on or after ${mcleodRouteStopLabel(stops, i)}.`,
        pathIndex: i + 1,
      };
    }
  }

  return { ok: true };
}

export function ensureMcleodRouteStops(
  values: Pick<NormalizedLoad, 'stops' | 'pickup' | 'consignee'>
): LoadStopsItem[] {
  const existing = values.stops ?? [];

  if (existing.length >= 2 && mcleodRouteEndsHaveContent(existing)) {
    return normalizeMcleodRouteStopTypes(existing);
  }

  if (
    mcleodFlatLocationHasRouteContent(values.pickup) ||
    mcleodFlatLocationHasRouteContent(values.consignee)
  ) {
    return buildMcleodStopsFromPickupAndConsignee(
      values.pickup,
      values.consignee
    );
  }

  if (existing.length >= 2) {
    return normalizeMcleodRouteStopTypes(existing);
  }

  return buildMcleodStopsFromPickupAndConsignee(
    values.pickup,
    values.consignee
  );
}
